/**
 *
 */
import {getAppConfig} from './config';
import {getDistance, googleGeolocateAddress} from '../utils/geolocation';
import {arrayToList} from '../utils/string';
import Glide from '@glidejs/glide';
import Cookies from 'js-cookie';

/**
 *
 * @typedef {{urls: {home: string}, images: {logos: {primary: {width: number, mimeType: string, url: string, height: number}, horizontal: {width: number, mimeType: string, url: string, height: number}}}, servicesOffered: {text: string, list: [{name: string, slug: string}]}, contactInfo: {primaryPhone: string, frontDeskPhone: string, salesPhone: string}, name: string, location: {address: {zip: string, city: string, streetAddress1: string, streetAddress2: string, state: {name: string, abbr: string}}, distance: {number: number, unit: string}, geolocation: {latitude: number, longitude: number}}, id: number, default: boolean, trackingCodes: {googleTagManagerIds: []|undefined}}} Community
 */


/**
 * @callback SuccessCallback
 * @param {GeolocationPosition} position
 */


/**
 * @callback FailureCallback
 */

/**
 * - populates closest communities based on user geolocation & provides location search form functionality
 */
class CommunityFinder
{
	/**
	 *
	 */
	constructor()
	{
		/**
		 * @type {[Community]}
		 */
		this.communities = [];

		/**
		 *
		 * @type {{latitude: number, longitude: number}|null}
		 */
		this.userLocation = null;

		/**
		 *
		 * @type {boolean}
		 */
		this.sortedByLocation = false;

		/**
		 *
		 * @type {null|Community}
		 */
		this.closestCommunity = null;

		/**
		 * @type {[Glide]}
		 */
		this.glideInstances = [];


		const communitiesJson = document.querySelector( '#esl-communities' );
		const userLatitude    = parseFloat( Cookies.get( 'userLatitude' ) );
		const userLongitude   = parseFloat( Cookies.get( 'userLongitude' ) );
		const body            = document.querySelector( 'body' );
		const isSearchPage    = body.classList.contains( 'search' );
		const isHomePage      = window.location.pathname === '/' && !isSearchPage;

		if (communitiesJson instanceof HTMLElement) {
			this.communities = JSON.parse( communitiesJson.textContent );
		}

		if (!isHomePage && userLatitude && userLongitude) {
			this.userLocation = {
				latitude:  userLatitude,
				longitude: userLongitude
			};
		}
	}


	/**
	 *
	 */
	init()
	{
		const siteHeader = document.querySelector( '#site-header' );

		if (this.userLocation) {

			if (siteHeader instanceof HTMLElement) {

				siteHeader.classList.add( 'no-community-transition' );

				this.communities      = this.getSortedCommunitiesByDistanceToCoordinates( this.userLocation );
				this.closestCommunity = this.communities[0];
				this.sortedByLocation = true;
				this.draw( false );

				setTimeout( () => siteHeader.classList.remove( 'no-community-transition' ), 1000 );
			}

		} else {

			this.requestGeoLocation(
				() => null,
				() =>
				{
					this.communities.sort( ( a, b ) =>
					{
						if (a.default && !b.default) {
							return -1;
						} else if (!a.default && b.default) {
							return 1;
						}

						return 0;
					} );

					this.sortedByLocation = false;

					this.draw( false );
				}
			);
		}

		this.setupLocationFinderForm();
		this.draw();

		// if 'search' query variable is set, fill out location finder form and submit

		const params       = new URLSearchParams( window.location.search );
		const searchVar    = params.get( 'search' );
		const locationForm = document.querySelector( '#community-location-finder-form' );

		if (searchVar && searchVar.length && locationForm instanceof HTMLElement) {

			const locationField = document.querySelector( '#community-location' );

			if (locationField instanceof HTMLElement) {
				locationField.value = searchVar;
				locationForm.requestSubmit()
			}
		}
	}


	/**
	 *
	 */
	draw()
	{
		const headline1LocationKnown   = document.querySelector( '#nearest-communities-headline .location-known' );
		const headline1LocationUnknown = document.querySelector( '#nearest-communities-headline .location-unknown' );
		const headline2LocationKnown   = document.querySelector( '#community-location-finder-headline .location-known' );
		const headline2LocationUnknown = document.querySelector( '#community-location-finder-headline .location-unknown' );

		if (this.sortedByLocation) {

			if (headline1LocationKnown instanceof HTMLElement && headline1LocationUnknown instanceof HTMLElement) {
				headline1LocationKnown.classList.remove( 'hidden' );
				headline1LocationUnknown.classList.add( 'hidden' );
			}

			if (headline2LocationKnown instanceof HTMLElement && headline2LocationUnknown instanceof HTMLElement) {
				headline2LocationKnown.classList.remove( 'hidden' );
				headline2LocationUnknown.classList.add( 'hidden' );
			}

		} else {

			if (headline1LocationKnown instanceof HTMLElement && headline1LocationUnknown instanceof HTMLElement) {
				headline1LocationKnown.classList.add( 'hidden' );
				headline1LocationUnknown.classList.remove( 'hidden' );
			}

			if (headline2LocationKnown instanceof HTMLElement && headline2LocationUnknown instanceof HTMLElement) {
				headline2LocationKnown.classList.add( 'hidden' );
				headline2LocationUnknown.classList.remove( 'hidden' );
			}
		}

		this.populateCommunityLists();
		this.populateClosestCommunity();
		this.applyCommunitySliders();
	}


	/**
	 *
	 */
	populateClosestCommunity()
	{
		const container  = document.querySelector( '#closest-community' );
		const siteHeader = document.querySelector( '#site-header' );

		if (container instanceof HTMLElement && siteHeader instanceof HTMLElement) {

			const anchorElem = container.querySelector( 'a' );
			const nameElem   = container.querySelector( '.community' );

			if (this.closestCommunity) {

				nameElem.innerHTML = this.closestCommunity.name;
				anchorElem.href    = this.closestCommunity.urls.home;
				siteHeader.classList.remove( 'no-closest-community' );

			} else {

				siteHeader.classList.add( 'no-closest-community' );
			}
		}
	}


	/**
	 *
	 */
	populateCommunityLists()
	{
		const communityList  = document.querySelector( '#community-list' );
		const maxCommunities = 3;
		const communities    = this.communities.slice( 0, maxCommunities );

		if (communityList instanceof HTMLElement) {

			communityList.innerHTML = `
				<div class="glide">
					<div class="glide__track" data-glide-el="track">
						<ul class="glide__slides community-card-list">
						${communities.map( community => `
							<li class="glide__slide">
								<div class="community">
									<div class="logo">
										<img src="${community.images.logos.primary.url}" alt="${community.name}">
									</div>
									<p class="services">
										${arrayToList( community.servicesOffered.list.map( service => service.name ) )}
									</p>
									<div class="address">
										<p>
											${community.location.address.streetAddress1}
										</p>
										<p>
											${community.location.address.city},
											${community.location.address.state.abbr}
											${community.location.address.zip}
										</p>
										<p>
											${community.contactInfo.primaryPhone}
										</p>								
									</div>
									<div class="cta">
										<a href="${community.urls.home}" class="btn btn-primary btn-lg">
											Visit Site
										</a>
									</div>
								</div>
							</li>
						` ).join( '' )}
						</ul>
					</div>
					${communities.length > 1 ? `
					<div class="glide__bullets" data-glide-el="controls[nav]">
						${communities.map( ( community, index ) => `
							<button class="glide__bullet" data-glide-dir="=${index}" aria-label="Slide ${index}"></button>
						` ).join( '' )}
					</div>
					` : ''}
				</div>
			`;
		}

	}


	/**
	 *
	 * @param {HTMLElement} element
	 */
	applyCommunitySlider( element )
	{
		const bullets = element.querySelectorAll( '.glide__bullet' );
		const glide   = new Glide( element, {
			type:        'slider',
			perView:     3,
			gap:         24,
			rewind:      false,
			breakpoints: {
				960: {
					perView: 2,
					focusAt: 'center',
					gap:     24,
					peek:    {
						before: -136,
						after:  -136
					},
				}
			}
		} );

		glide.mount();


		// hide extraneous bullets on lg/desktop screens

		bullets.forEach( ( bullet, key ) =>
		{
			let hideDesktop = false;

			if (bullets.length <= 3) {
				hideDesktop = true;
			} else if (key > bullets.length - 3) {
				hideDesktop = true;
			}

			if (hideDesktop) {
				bullet.classList.add( 'd-lg-none' );
			}
		} );

		return glide;
	}


	/**
	 *
	 */
	applyCommunitySliders()
	{
		const elements = document.querySelectorAll( '.community-slider .glide' );

		this.glideInstances.forEach( glide => glide.destroy() );

		this.glideInstances = [];

		if (elements.length) {

			elements.forEach( elem =>
			{
				const glide = this.applyCommunitySlider( elem );

				this.glideInstances.push( glide );
			} );
		}
	}


	/**
	 *
	 */
	setupLocationFinderForm()
	{
		const appConfig          = getAppConfig();
		const communityList      = document.querySelector( '#community-list' );
		const form               = document.querySelector( '#community-location-finder-form' );
		const searchResultsInfo  = document.querySelector( '#community-search-results-info' );
		const searchResultsClear = document.querySelector( '#community-search-results-clear' );
		const allLocations       = document.querySelector( '#all-locations' );

		if (form instanceof HTMLElement) {

			form.addEventListener( 'submit', e =>
			{
				const inputElem = document.querySelector( '#community-location' );

				e.preventDefault();
				form.removeAttribute( 'novalidate' );


				if (inputElem instanceof HTMLElement) {

					const address = inputElem.value.trim();

					inputElem.classList.remove( 'is-invalid' );

					if (!address.length) {

						form.setAttribute( 'novalidate', 'true' );
						inputElem.classList.add( 'is-invalid' );

					} else {

						if (appConfig && appConfig.googleMapsApiKey) {

							googleGeolocateAddress(
								address,
								appConfig.googleMapsApiKey,
								coords =>
								{
									this.communities      = this.getSortedCommunitiesByDistanceToCoordinates( coords );
									this.sortedByLocation = true;
									this.draw();

									if (searchResultsInfo instanceof HTMLElement) {

										const term = searchResultsInfo.querySelector( '.term' );

										if (term instanceof HTMLElement) {
											term.innerHTML = address;
										}

										searchResultsInfo.classList.remove( 'hidden' );
									}

									if (allLocations instanceof HTMLElement) {
										allLocations.classList.add( 'hidden' );
									}

									if (communityList instanceof HTMLElement) {

										communityList.classList.remove( 'hidden' );

										this.applyCommunitySlider( communityList );

										communityList.scrollIntoView( false );
									}
								},
								() =>
								{
									form.setAttribute( 'novalidate', 'true' );
									inputElem.classList.add( 'is-invalid' );
								}
							);
						}
					}
				}
			} );
		}


		if (searchResultsClear instanceof HTMLElement) {
			searchResultsClear.addEventListener( 'click', e =>
			{
				e.preventDefault();

				if (communityList instanceof HTMLElement) {
					communityList.classList.add( 'hidden' );
				}

				if (searchResultsInfo instanceof HTMLElement) {
					searchResultsInfo.classList.add( 'hidden' );
				}

				if (allLocations instanceof HTMLElement) {
					allLocations.classList.remove( 'hidden' );
				}

				this.applyCommunitySliders();

				form.scrollIntoView( true );
			} );
		}
	}


	/**
	 *
	 * @param {{latitude: number, longitude: number}} coords
	 */
	getSortedCommunitiesByDistanceToCoordinates( coords )
	{
		return this.communities
			.map( community =>
			{
				const distance = getDistance( coords, community.location.geolocation );

				community.location.distance = {
					number: Math.round( distance * 10 ) / 10,
					unit:   'miles'
				};

				return community;
			} )
			.sort( ( a, b ) =>
			{
				return a.location.distance.number < b.location.distance.number ? -1 : 1
			} );
	}


	/**
	 *
	 * @param {SuccessCallback} successCallback
	 * @param {FailureCallback} failureCallback
	 */
	requestGeoLocation( successCallback = () => null, failureCallback = () => null )
	{
		if ('geolocation' in navigator) {

			navigator.geolocation.getCurrentPosition(
				position =>
				{
					this.userLocation     = {
						latitude:  position.coords.latitude,
						longitude: position.coords.longitude
					};
					this.communities      = this.getSortedCommunitiesByDistanceToCoordinates( this.userLocation );
					this.sortedByLocation = true;
					this.closestCommunity = this.communities[0];

					Cookies.set( 'userLatitude', this.userLocation.latitude );
					Cookies.set( 'userLongitude', this.userLocation.longitude );

					this.draw( false );
					successCallback( position );
				},
				() => failureCallback()
			);

		} else {

			failureCallback();
		}
	}
}


/**
 *
 */
const setupCommunityFinder = () =>
{
	const communityFinder = new CommunityFinder;

	communityFinder.init();
};


/**
 *
 */
export default setupCommunityFinder;